import { cn, isRawRank, type RawRank } from "@tcg/ui";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { CameraCapture } from "@/components/CameraCapture";
import { MoneyText } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import { RankPicker } from "@/features/buylist/RankPicker";
import { ItemBadges } from "@/features/sale/ItemBadges";
import { call } from "@/lib/api";
import type { ConsignmentIntakeLineIn } from "@/lib/consignment-types";
import { uploadCardPhoto } from "@/lib/upload";

export type ConsignProductType = ConsignmentIntakeLineIn["productType"];

export interface ConsignLine {
  key: string;
  productType: ConsignProductType;
  title: string;
  rawRank: RawRank | null;
  gradingCompany: string;
  grade: string;
  certNumber: string;
  quantity: number;
  /** 販売価格 税込 (digits). */
  price: string;
  /** 最低価格 税込 (digits, optional). */
  minPrice: string;
  commission: string;
  discountAllowed: boolean;
  features: string;
  /** Front / back photos already uploaded (media asset ids). */
  photoIds?: string[];
  /** 未開封 (sealed): the box's weight in grams (digits; empty = the shop's default — T08-37). */
  weightG: string;
}

/** consignment-service's range of `weightG` (IntakeLineIn, T08-37). */
export const WEIGHT_MIN_G = 1;
export const WEIGHT_MAX_G = 100_000;

export const MAX_DISCOUNT_PERCENT = 10;

export function emptyLine(
  key: string,
  productType: ConsignProductType = "raw_single",
): ConsignLine {
  return {
    key,
    productType,
    title: "",
    rawRank: null,
    gradingCompany: "PSA",
    grade: "",
    certNumber: "",
    quantity: 1,
    price: "",
    minPrice: "",
    commission: "",
    discountAllowed: false,
    features: "",
    weightG: "",
  };
}

/** The typed weight (full-width digits too) → grams, or null when empty / not a whole number. */
export function weightGrams(value: string): number | null {
  const digits = value.normalize("NFKC").trim();
  if (!/^\d+$/.test(digits)) {
    return null;
  }
  return Number.parseInt(digits, 10);
}

export function lineProblems(line: ConsignLine): string[] {
  const out: string[] = [];
  if (!line.title.trim()) {
    out.push("title");
  }
  if (line.productType === "raw_single" && !line.rawRank) {
    out.push("rank");
  }
  if (line.productType === "graded_single" && (!line.gradingCompany.trim() || !line.grade.trim())) {
    out.push("grade");
  }
  if (!line.price) {
    out.push("price");
  }
  if (line.productType === "sealed_product" && line.weightG.trim()) {
    const grams = weightGrams(line.weightG);
    if (grams === null || grams < WEIGHT_MIN_G || grams > WEIGHT_MAX_G) {
      out.push("weight");
    }
  }
  return out;
}

const yenIn = (digits: string) => ({
  amount: String(Number.parseInt(digits, 10)),
  currencyCode: "JPY" as const,
});

export function lineBody(line: ConsignLine): ConsignmentIntakeLineIn {
  const commission = line.commission.trim();
  // sealed only; empty = the shop's default weight (CONSIGNMENT_SEALED_DEFAULT_WEIGHT_G)
  const grams = line.productType === "sealed_product" ? weightGrams(line.weightG) : null;
  return {
    productType: line.productType,
    catalogTitle: line.title.trim(),
    quantity: line.productType === "sealed_product" ? line.quantity : 1,
    proposedPrice: yenIn(line.price),
    discountAllowed: line.discountAllowed,
    maxDiscountPercent: MAX_DISCOUNT_PERCENT,
    ...(line.productType === "raw_single" && line.rawRank ? { rawRank: line.rawRank } : {}),
    ...(line.productType === "graded_single"
      ? {
          gradingCompany: line.gradingCompany.trim(),
          grade: line.grade.trim(),
          ...(line.certNumber.trim() ? { certNumber: line.certNumber.trim() } : {}),
        }
      : {}),
    ...(line.minPrice ? { agreedMinPrice: yenIn(line.minPrice) } : {}),
    ...(commission ? { commissionPercentOverride: commission } : {}),
    ...(line.features.trim() ? { features: line.features.trim() } : {}),
    ...(line.photoIds?.length ? { photoMediaIds: line.photoIds } : {}),
    ...(grams !== null ? { weightG: grams } : {}),
  };
}

const TYPES: ConsignProductType[] = ["raw_single", "graded_single", "sealed_product"];

/**
 * One card taken in (T07-32): kind, name, **Rank A–D for a raw card (required)**, grade + cert, the
 * selling price 税込 (NumPad), the consignor's lowest price (optional), a commission other than the
 * contract's (optional), 「値引き可（10%まで）」, 特徴 (for the 古物台帳); a sealed box: how many + its
 * weight in grams (T07-42 / T08-37 — empty = the shop's default weight). The 委託品 of a private
 * person is 「委託品／インボイス対象外」 on the receipt — the shop only earns its commission.
 */
export function IntakeLineForm({
  onAdd,
  nextKey,
}: {
  onAdd: (line: ConsignLine) => void;
  nextKey: () => string;
}) {
  const { t } = useTranslation();
  const { api, media } = usePos();
  const [line, setLine] = useState<ConsignLine>(() => emptyLine(nextKey()));
  const [front, setFront] = useState<Blob | null>(null);
  const [back, setBack] = useState<Blob | null>(null);
  const [uploading, setUploading] = useState(false);
  const set = (changes: Partial<ConsignLine>) => setLine((l) => ({ ...l, ...changes }));
  const problems = lineProblems(line);

  // a scanned slab cert / a known code in the name field fills the line (catalog lookup)
  const scanned = async (code: string) => {
    const value = code.trim();
    if (!value) {
      return;
    }
    try {
      const hit = await call(() =>
        api.GET("/v1/pos/lookup", { params: { query: { code: value } } }),
      );
      const v = hit.variant;
      set({
        title: v.title.ja ?? v.title.en ?? value,
        productType:
          v.productType === "graded_single"
            ? "graded_single"
            : v.productType === "sealed"
              ? "sealed_product"
              : "raw_single",
        rawRank: isRawRank(v.rawRank) ? v.rawRank : null,
        gradingCompany: v.grading?.company ?? line.gradingCompany,
        grade: v.grading?.grade ?? "",
        certNumber: v.grading?.certNumber ?? (/^\d{7,10}$/.test(value) ? value : ""),
      });
    } catch {
      if (/^\d{7,10}$/.test(value)) {
        set({ title: "", productType: "graded_single", rawRank: null, certNumber: value });
      }
    }
  };

  const add = async () => {
    if (problems.length || uploading) {
      return;
    }
    setUploading(true);
    try {
      const photoIds: string[] = [];
      for (const photo of [front, back]) {
        if (photo) {
          photoIds.push((await uploadCardPhoto(media, photo)).assetId);
        }
      }
      onAdd({ ...line, photoIds });
      setLine(emptyLine(nextKey(), line.productType));
      setFront(null);
      setBack(null);
    } finally {
      setUploading(false);
    }
  };
  const red = line.productType === "raw_single" && !line.rawRank;
  return (
    <section
      aria-label={t("consign.new_line")}
      data-invalid={red ? "true" : undefined}
      className={cn(
        "flex flex-col gap-3 rounded-lg border p-3",
        red && "border-2 border-status-problem bg-status-problem/5",
      )}
    >
      <fieldset className="flex flex-wrap gap-2">
        <legend className="mb-1 font-medium">{t("buylist.kind")}</legend>
        {TYPES.map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={line.productType === type}
            onClick={() =>
              set({ productType: type, rawRank: type === "raw_single" ? line.rawRank : null })
            }
            className={cn(
              "min-h-pos-touch rounded-md border px-4 text-lg",
              line.productType === type && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {t(`consign.kind_${type}`)}
          </button>
        ))}
      </fieldset>
      <label className="flex flex-col gap-1">
        <span className="font-medium">{t("buylist.title")}</span>
        <input
          value={line.title}
          onChange={(e) => set({ title: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void scanned(e.currentTarget.value);
            }
          }}
          autoComplete="off"
          enterKeyHint="search"
          placeholder={t("buylist.title_placeholder")}
          className="min-h-pos-touch rounded-md border px-3 text-lg"
        />
      </label>
      {line.productType === "raw_single" ? (
        <RankPicker value={line.rawRank} onChange={(rank) => set({ rawRank: rank })} />
      ) : null}
      {line.productType === "graded_single" ? (
        <div className="grid grid-cols-3 gap-2">
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.grader")}</span>
            <select
              value={line.gradingCompany}
              onChange={(e) => set({ gradingCompany: e.target.value })}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            >
              {["PSA", "BGS", "CGC", "ARS"].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.grade")}</span>
            <input
              value={line.grade}
              onChange={(e) => set({ grade: e.target.value })}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.cert")}</span>
            <input
              value={line.certNumber}
              onChange={(e) => set({ certNumber: e.target.value })}
              inputMode="numeric"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
        </div>
      ) : null}
      {line.productType === "sealed_product" ? (
        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex max-w-xs flex-col gap-1">
            <span className="font-medium">{t("buylist.quantity")}</span>
            <input
              type="number"
              min={1}
              max={50}
              value={line.quantity}
              onChange={(e) =>
                set({
                  quantity: Math.max(1, Math.min(50, Number.parseInt(e.target.value || "1", 10))),
                })
              }
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <div className="flex flex-col gap-1">
            <label htmlFor={`consign-weight-${line.key}`} className="font-medium">
              {t("consign.weight")}
            </label>
            <span className="flex items-center gap-2">
              <input
                id={`consign-weight-${line.key}`}
                value={line.weightG}
                onChange={(e) => set({ weightG: e.target.value.replace(/[^\d０-９]/g, "") })}
                inputMode="numeric"
                autoComplete="off"
                placeholder={t("consign.weight_placeholder")}
                aria-describedby={`consign-weight-hint-${line.key}`}
                className="min-h-pos-touch w-40 rounded-md border px-3 text-lg tabular-nums"
              />
              <span aria-hidden>g</span>
            </span>
            <span id={`consign-weight-hint-${line.key}`} className="text-sm text-muted-foreground">
              {t("consign.weight_hint")}
            </span>
          </div>
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-[minmax(0,16rem)_1fr]">
        <NumPad
          label={t("consign.price")}
          value={line.price}
          onChange={(v) => set({ price: v })}
          money
        />
        <div className="flex flex-col gap-3">
          <p className="rounded-md bg-muted p-3 text-sm">{t("consign.price_note")}</p>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("consign.min_price")}</span>
            <input
              value={line.minPrice}
              onChange={(e) => set({ minPrice: e.target.value.replace(/\D/g, "") })}
              inputMode="numeric"
              placeholder={t("consign.optional")}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("consign.commission_override")}</span>
            <input
              value={line.commission}
              onChange={(e) => set({ commission: e.target.value.replace(/[^\d.]/g, "") })}
              inputMode="decimal"
              placeholder={t("consign.commission_contract")}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <label className="flex min-h-12 items-center gap-3">
            <input
              type="checkbox"
              checked={line.discountAllowed}
              onChange={(e) => set({ discountAllowed: e.target.checked })}
              className="size-6"
            />
            {t("consign.discount_allowed", { percent: MAX_DISCOUNT_PERCENT })}
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.features")}</span>
            <input
              value={line.features}
              onChange={(e) => set({ features: e.target.value })}
              placeholder={t("buylist.features_placeholder")}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <details>
            <summary className="min-h-12 cursor-pointer py-2 font-medium">
              {t("buylist.photos")}
            </summary>
            <div className="grid gap-3 md:grid-cols-2">
              <CameraCapture label={t("buylist.photo_front")} photo={front} onPhoto={setFront} />
              <CameraCapture label={t("buylist.photo_back")} photo={back} onPhoto={setBack} />
            </div>
          </details>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void add()}
          disabled={problems.length > 0 || uploading}
          aria-describedby={problems.length ? "consign-line-problems" : undefined}
          className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          <Plus aria-hidden className="size-6" />
          {uploading ? t("app.saving") : t("consign.add_line")}
        </button>
        {problems.length ? (
          <span id="consign-line-problems" className="text-pos-problem">
            {t(`consign.problem_${problems[0] ?? "title"}`)}
          </span>
        ) : null}
      </div>
    </section>
  );
}

export function IntakeLineRow({ line, onRemove }: { line: ConsignLine; onRemove: () => void }) {
  const { t } = useTranslation();
  const price = Number.parseInt(line.price || "0", 10);
  return (
    <li className="flex items-center gap-3 border-b px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">
          {line.title}
          {line.productType === "sealed_product" && line.quantity > 1
            ? ` ×${String(line.quantity)}`
            : ""}
        </p>
        <ItemBadges
          productType={line.productType === "sealed_product" ? "sealed" : line.productType}
          rawRank={line.rawRank}
          grading={
            line.productType === "graded_single"
              ? { company: line.gradingCompany, grade: line.grade, certNumber: line.certNumber }
              : null
          }
          consigned
          size="sm"
        />
        {line.productType === "sealed_product" && weightGrams(line.weightG) !== null ? (
          <span className="ml-2 text-sm text-muted-foreground tabular-nums">
            {t("consign.weight_row", { grams: weightGrams(line.weightG) })}
          </span>
        ) : null}
        {line.discountAllowed ? (
          <span className="ml-2 text-sm text-muted-foreground">
            {t("consign.discount_allowed", { percent: MAX_DISCOUNT_PERCENT })}
          </span>
        ) : null}
      </div>
      <MoneyText amount={price} size="lg" />
      <button
        type="button"
        aria-label={t("buylist.remove", { title: line.title })}
        onClick={onRemove}
        className="flex size-11 items-center justify-center rounded-md border"
      >
        <Trash2 aria-hidden className="size-5" />
      </button>
    </li>
  );
}
